import React, { useCallback, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import PaymentPanel from "components/PaymentOptionsCard";
import PaymentFormManualOrder from "components/PaymentFormManualOrder";
import { showError, showSuccess } from "toast/toast";
import { safeConsole } from "utils/envUtils";
import {
  buildCheckoutPaymentInfoRow,
  clearCheckoutCartAfterPayment,
  completeCheckoutPayment,
  getCheckoutPaymentMetaFromOrder,
  resolveCheckoutOrderId,
} from "./completeCheckoutPayment";
import {
  fetchSalesOrderForPaidFinalize,
  finalizeSalesOrderTaxAfterPaid,
} from "orderResolve";
import { useCheckoutPayment } from "./useCheckoutPayment";
import { checkoutStripeAppearance } from "./checkoutPaymentTheme";

export type CheckoutPaymentVariant = "agent" | "realtor";

export type CheckoutPaymentContentProps = {
  open: boolean;
  token: string;
  variant: CheckoutPaymentVariant;
  order: Record<string, unknown> | null;
  totalAmount: number;
  supplierId: string;
  payerUserId: string;
  payerEmail: string;
  paidById: string;
  paidByName: string;
  onPaymentComplete: (orderId: string) => void;
  onClearCart: () => void;
};

const CheckoutPaymentContent: React.FC<CheckoutPaymentContentProps> = ({
  open,
  token,
  variant,
  order,
  totalAmount,
  supplierId,
  payerUserId,
  payerEmail,
  paidById,
  paidByName,
  onPaymentComplete,
  onClearCart,
}) => {
  const orderId = resolveCheckoutOrderId(order);
  const paymentMeta = useMemo(
    () =>
      getCheckoutPaymentMetaFromOrder(order, {
        payerEmail,
        paidByName,
      }),
    [order, payerEmail, paidByName]
  );
  const [submitting, setSubmitting] = useState(false);

  const {
    dynamicMethods,
    stripePromise,
    clientSecret,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    loading,
  } = useCheckoutPayment({
    token,
    supplierId,
    payerUserId,
    orderId,
    totalAmount,
    enabled: open && Boolean(orderId),
  });

  const recordDynamicPayment = useCallback(
    async (method: { name: string; _id: string }) => {
      if (!orderId) return;
      setSubmitting(true);
      try {
        const paymentBody = buildCheckoutPaymentInfoRow({
          method: method.name,
          totalAmount,
          amountPaid: 0,
          paymentStatus: "Pending",
          paidBy: { id: paidById, name: paidByName },
        });
        await completeCheckoutPayment({
          orderId,
          token,
          paymentBody,
        });
        onClearCart();
        showSuccess(`Order placed with ${method.name}.`);
        onPaymentComplete(orderId);
      } catch (err) {
        showError(err instanceof Error ? err.message : "Failed to record payment");
      } finally {
        setSubmitting(false);
      }
    },
    [
      orderId,
      token,
      totalAmount,
      paidById,
      paidByName,
      onClearCart,
      onPaymentComplete,
    ]
  );

  const recordStripePayment = useCallback(
    async (paymentIntentId: string) => {
      if (!orderId) return;
      setSubmitting(true);
      try {
        const paymentBody = buildCheckoutPaymentInfoRow({
          method: selectedPaymentMethod?.name || "online",
          totalAmount,
          amountPaid: totalAmount,
          paymentId: paymentIntentId,
          paymentStatus: "Paid",
          paidBy: { id: paidById, name: paidByName },
        });
        await completeCheckoutPayment({
          orderId,
          token,
          paymentBody,
        });

        try {
          const freshOrder = (await fetchSalesOrderForPaidFinalize(token, orderId)) ?? order;
          if (freshOrder) {
            await finalizeSalesOrderTaxAfterPaid({
              token,
              orderId,
              order: freshOrder,
            });
          }
        } catch (finalizeErr) {
          safeConsole.warn(
            "[Checkout] Tax finalize after payment failed (non-blocking):",
            finalizeErr
          );
        }

        onClearCart();
        showSuccess("Payment completed successfully!");
        onPaymentComplete(orderId);
      } catch (err) {
        showError(err instanceof Error ? err.message : "Failed to record payment");
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [
      orderId,
      token,
      totalAmount,
      selectedPaymentMethod?.name,
      paidById,
      paidByName,
      order,
      onClearCart,
      onPaymentComplete,
    ]
  );

  if (!orderId) {
    return <p className="text-muted mb-0">Order is not ready for payment.</p>;
  }

  return (
    <div className="checkout-payment-content">
      {loading && (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm" role="status" />
        </div>
      )}

      <PaymentPanel
        variant={variant}
        remainingAmount={totalAmount}
        orderStatus={100}
        paymentStatus="Pending"
        compactLayout
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        maxAllowed={totalAmount}
        dynamicMethods={dynamicMethods.map((m) => ({
          _id: String(m._id || m.id || m.name),
          name: String(m.name || ""),
        }))}
        paymentInfo={[]}
        onSubmitOffline={recordDynamicPayment}
        onlinePaymentNode={
          stripePromise && clientSecret ? (
            <Elements
              key={clientSecret}
              stripe={stripePromise}
              options={{ clientSecret, appearance: checkoutStripeAppearance }}
            >
              <PaymentFormManualOrder
                url="orders"
                method="PUT"
                supplierId={supplierId}
                totalAmount={totalAmount}
                paidAmount={totalAmount}
                orderId={orderId}
                orderNumber={paymentMeta.orderNumber}
                customerName={paymentMeta.customerName}
                customerPhone={paymentMeta.customerPhone}
                selectedPaymentMethod={selectedPaymentMethod?.name || "online"}
                userId={payerUserId}
                userEmail={paymentMeta.customerEmail || payerEmail}
                paidBy_id={paidById}
                paidBy_name={paidByName}
                payments_status="Paid"
                useCustomPaymentRecord
                skipRedirectOnSuccess
                embedded
                savedCardsLayout="row"
                onCustomPaymentRecord={recordStripePayment}
                onPaymentSuccess={async () => {}}
              />
            </Elements>
          ) : (
            <p className="text-muted small mb-0">Loading card payment…</p>
          )
        }
      />

      {submitting && (
        <div className="text-center mt-2">
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
        </div>
      )}

      <style>{`
        .checkout-payment-content .card.shadow-lg {
          box-shadow: none !important;
          border: 0;
        }
        .checkout-payment-content .card-body {
          padding: 0;
        }
        .checkout-payment-content .order-card-title.small {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 10px;
        }
        .checkout-payment-content .method-row.compact-method-bar {
          border: 1.5px solid #c7ccd4;
          border-radius: 9px;
          padding: 10px 14px;
          margin-bottom: 14px;
          box-shadow: none;
        }
        .checkout-payment-content .method-row.compact-method-bar.active {
          border-color: #c7ccd4;
          box-shadow: none;
        }
        .checkout-payment-content .method-row.compact-method-bar .form-check-input {
          width: 15px;
          height: 15px;
          margin-top: 0;
          border: 2px solid #3B6FE8;
          background-color: #fff;
        }
        .checkout-payment-content .method-row.compact-method-bar .form-check-input:checked {
          background-color: #3B6FE8 !important;
          border-color: #3B6FE8 !important;
          background-image: none;
          box-shadow: inset 0 0 0 3px #fff;
        }
        .checkout-payment-content .method-row.compact-method-bar .form-check-label {
          font-size: 14px;
          font-weight: 500;
          color: #111827;
        }
        .checkout-payment-content .compact-online-node {
          margin-left: 0 !important;
          margin-top: 0 !important;
        }
        .checkout-payment-content .paymentcards {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }
        .checkout-payment-content .paymentcards > [class*="col-"] {
          flex: 1 1 calc(50% - 12px);
          max-width: calc(50% - 12px);
        }
        @media (max-width: 768px) {
          .checkout-payment-content .paymentcards > [class*="col-"] {
            flex: 1 1 100%;
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default CheckoutPaymentContent;
